import React, { useMemo } from 'react';
import { useSessionStore } from '../../stores/sessionStore';
import { toFlopzillaNotation } from './RangeNotation';
import type { HandRecord, ActionRecord } from '@range-reader/shared';
import { PlayerTooltip } from '../players/PlayerTooltip';

const STREETS = ['preflop', 'flop', 'turn', 'river'] as const;
type StreetKey = typeof STREETS[number];

const STREET_COLORS: Record<StreetKey, string> = {
  preflop: '#6366f1',
  flop:    '#10b981',
  turn:    '#f59e0b',
  river:   '#ef4444',
};

function getActionColor(action: ActionRecord['action']): string {
  switch (action) {
    case 'fold':   return '#6b7280';
    case 'check':  return '#9ca3af';
    case 'call':   return '#3b82f6';
    case 'bet':    return '#f59e0b';
    case 'raise':  return '#f59e0b';
    case 'allin':  return '#ef4444';
    default:       return '#9ca3af';
  }
}

function formatAmount(amount: number, bigBlind: number): string {
  if (!amount || amount <= 0) return '';
  if (!bigBlind || bigBlind <= 0) return ` $${(amount / 100).toFixed(2)}`;
  const bbs = amount / bigBlind;
  const formatted = bbs % 1 === 0 ? `${bbs}` : bbs.toFixed(1);
  return ` ${formatted}bb`;
}

function getBoardForStreet(communityCards: string, street: StreetKey): string {
  const cards = communityCards.trim().split(/\s+/).filter(Boolean);
  if (street === 'flop')  return cards.slice(0, 3).join(' ');
  if (street === 'turn')  return cards.slice(0, 4).join(' ');
  if (street === 'river') return cards.slice(0, 5).join(' ');
  return '';
}

interface Props {
  hand: HandRecord;
  eliminationsByStreet: Record<StreetKey, string[]>;
  rangeWidthByStreet: Record<string, number | null>;
}

export function HandHistoryColumn({ hand, eliminationsByStreet, rangeWidthByStreet }: Props) {
  // Build name → position map
  const nameToPos = useMemo(() => {
    const m: Record<string, string> = {};
    for (const p of hand.players) m[p.name] = p.position;
    return m;
  }, [hand]);

  // Group actions by street
  const actionsByStreet = useMemo(() => {
    const grouped: Record<StreetKey, ActionRecord[]> = {
      preflop: [], flop: [], turn: [], river: [],
    };
    for (const action of hand.actions) {
      const s = action.street as StreetKey;
      if (s in grouped) grouped[s].push(action);
    }
    return grouped;
  }, [hand]);

  return (
    <div className="history-column">
      <h3 className="history-col-title">Hand History</h3>
      <div className="history-streets">
        {STREETS.map(street => {
          const actions = actionsByStreet[street];
          const eliminated = eliminationsByStreet[street];
          const rangeWidth = rangeWidthByStreet[street];
          const board = getBoardForStreet(hand.communityCards, street);
          const notation = eliminated.length > 0 ? toFlopzillaNotation(eliminated) : null;

          if (actions.length === 0 && !notation && rangeWidth == null) return null;

          return (
            <div key={street} className="history-street-block">
              {/* Street header row */}
              <div className="history-street-header">
                <span className="history-street-badge" style={{ background: STREET_COLORS[street] }}>
                  {street.toUpperCase()}
                </span>
                {board && <span className="history-board-cards">{board}</span>}
                {rangeWidth != null && (
                  <span className="history-range-width">{rangeWidth} hands</span>
                )}
              </div>

              {/* Actions */}
              {actions.length > 0 && (
                <div className="history-actions-list">
                  {actions.map((a, i) => {
                    const pos = nameToPos[a.player] ?? a.player;
                    const amountStr = formatAmount(a.amount, hand.bigBlind);
                    const actionLabel = a.action === 'allin' ? 'all-in' : a.action;
                    return (
                      <div key={i} className="history-action-row">
                        <PlayerTooltip name={a.player}><span className="history-pos">{pos}</span></PlayerTooltip>
                        <span className="history-verb" style={{ color: getActionColor(a.action) }}>
                          {actionLabel}{amountStr}
                        </span>
                      </div>
                    );
                  })}
                </div>
              )}

              {/* Eliminated hands in Flopzilla notation */}
              {notation && (
                <div className="history-eliminations">
                  <span className="history-elim-icon">✕</span>
                  <span className="history-elim-notation">{notation}</span>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
